import { createStore } from "zustand/vanilla";
import { between, deepEqual, ok } from "@pack-it-up/shared";
import { applyToggle, clearCompletions, completionDelta, deriveModified } from "@pack-it-up/core";
import { emptyReconcile, onSnapshot, list, get as rget, } from "./reconcile.js";
import { memoizedOverSlice } from "./memo.js";
import { createOptimisticRunner } from "./optimistic.js";
import { errorToMessage } from "../errorToMessage.js";
// The participant paths currently marked done on a todo — the writes a clear must undo.
const doneParticipants = (t) => Object.entries(t.completions.byParticipant)
    .filter(([, entry]) => entry.done)
    .map(([pid]) => pid);
// Replace a single todo in a reconcile slice (used by the local optimistic clear/undo).
const replaceTodo = (s, todo) => {
    const byId = new Map(s.byId);
    byId.set(todo.id, todo);
    return { byId, pending: s.pending };
};
export const createTodosStore = (deps) => {
    const rows = memoizedOverSlice((s) => list(s));
    // Stream subscriptions are owned by the store: dispose() unsubscribes them all, so a
    // superseded store stops writing into state nothing renders. `subs` is filled after
    // the store exists (the streams push into it).
    const subs = [];
    const store = createStore((set, getState) => {
        const runner = createOptimisticRunner({
            entity: "todo",
            deltaPrefix: "todo-delta",
            getSlice: () => getState().slice,
            set,
            ...(deps.writeTimeoutMs !== undefined ? { timeoutMs: deps.writeTimeoutMs } : {}),
        });
        const optimistic = runner.mutate;
        return {
            slice: emptyReconcile(),
            error: null,
            undoSnapshot: null,
            todos: () => rows(getState().slice),
            async create(input) {
                const r = await deps.todos.createTodo(input);
                if (!r.ok)
                    set({ error: errorToMessage(r.error) });
                return r;
            },
            update: (patch) => {
                // Set by the mutator below when the edit flips the cached provenance flag, so
                // the same write that persists the content also persists the flag (Doc 02 §7).
                let modified;
                return optimistic(patch.todoId, (cur) => {
                    const next = { ...cur };
                    if (patch.name !== undefined)
                        next.name = patch.name;
                    // `null` clears the field (Doc 12 §1.3); a string sets it. A manual due-date
                    // *set* goes through pinDueDate; update handles the clear (dueDate: null).
                    if (patch.description !== undefined) {
                        if (patch.description === null)
                            delete next.description;
                        else
                            next.description = patch.description;
                    }
                    if (patch.dueDate !== undefined) {
                        if (patch.dueDate === null)
                            delete next.dueDate;
                        else
                            next.dueDate = patch.dueDate;
                    }
                    // Template todos (Doc 07 §4): offset relative to trip start.
                    if (patch.relativeDueOffset !== undefined) {
                        if (patch.relativeDueOffset === null)
                            delete next.relativeDueOffset;
                        else
                            next.relativeDueOffset = patch.relativeDueOffset;
                    }
                    if (patch.notes !== undefined) {
                        if (patch.notes === null)
                            delete next.notes;
                        else
                            next.notes = patch.notes;
                    }
                    // Tags are stored absent rather than empty. They are snapshot content
                    // (T29), so a tag-only edit does flip modifiedSinceImport below.
                    if (patch.tags !== undefined) {
                        if (patch.tags === null || patch.tags.length === 0)
                            delete next.tags;
                        else
                            next.tags = patch.tags;
                    }
                    // Re-derive the cached flag against the import snapshot (Doc 02 §7, Doc 06
                    // §10): editing raises it, editing back to the imported content clears it.
                    if (next.provenance) {
                        const m = deriveModified(next);
                        if (m !== next.provenance.modifiedSinceImport) {
                            modified = m;
                            next.provenance = { ...next.provenance, modifiedSinceImport: m };
                        }
                    }
                    return next;
                }, () => deps.todos.updateTodo(modified === undefined ? patch : { ...patch, modifiedSinceImport: modified }));
            },
            async toggle(todoId, target, targetIsUser) {
                const cur = rget(getState().slice, todoId);
                if (!cur)
                    return null;
                const res = applyToggle(cur, deps.actor, target, targetIsUser, {
                    allowProxyCompletion: deps.allowProxyCompletion?.() ?? false,
                });
                if (!res.ok) {
                    set({ error: "You can't check another person's box." });
                    return null;
                }
                const writes = completionDelta(cur.completions, res.item.completions);
                return optimistic(todoId, () => res.item, async () => {
                    const results = await Promise.all(writes.map(([pid, done]) => deps.todos.setCompletion(todoId, pid, done)));
                    return results.find((r) => !r.ok) ?? ok(undefined);
                });
            },
            setAssignment: (todoId, assignment) => optimistic(todoId, (cur) => ({ ...cur, assignment }), () => deps.todos.setAssignment(todoId, assignment)),
            // Pinning freezes the date against recompute (Doc 07 §4).
            pinDueDate: (todoId, dueDate) => optimistic(todoId, (cur) => ({ ...cur, dueDate, dueDateManuallyOverridden: true }), () => deps.todos.pinDueDate(todoId, dueDate)),
            move: (todoId, toCategoryId, neighbors) => {
                const order = between(neighbors.before ?? null, neighbors.after ?? null);
                return optimistic(todoId, (cur) => ({ ...cur, categoryId: toCategoryId, order }), () => deps.todos.moveTodo(todoId, toCategoryId, order));
            },
            async clearCompleted() {
                const before = list(getState().slice);
                const cleared = clearCompletions(before, "all");
                const clearedById = new Map(cleared.map((t) => [t.id, t]));
                // Optimistically clear each todo whose completions actually change, recording the
                // per-participant inverse so Undo can re-check exactly those.
                const undoRows = [];
                let next = getState().slice;
                for (const t of before) {
                    const after = clearedById.get(t.id);
                    if (deepEqual(after.completions, t.completions))
                        continue;
                    undoRows.push({ todoId: t.id, restore: doneParticipants(t) });
                    next = replaceTodo(next, after);
                }
                set({ slice: next, undoSnapshot: undoRows, error: null });
                await Promise.all(undoRows.flatMap((u) => u.restore.map((p) => deps.todos.setCompletion(u.todoId, p, false))));
            },
            async undo() {
                const undoRows = getState().undoSnapshot;
                if (!undoRows)
                    return;
                set({ undoSnapshot: null, error: null });
                let next = getState().slice;
                for (const u of undoRows) {
                    const cur = rget(next, u.todoId);
                    if (!cur)
                        continue;
                    const byParticipant = { ...cur.completions.byParticipant };
                    for (const p of u.restore)
                        byParticipant[p] = { done: true, checkedBy: p };
                    next = replaceTodo(next, { ...cur, completions: { byParticipant } });
                }
                set({ slice: next });
                await Promise.all(undoRows.flatMap((u) => u.restore.map((p) => deps.todos.setCompletion(u.todoId, p, true))));
            },
            remove: (todoId) => runner.remove(todoId, () => deps.todos.deleteTodo(todoId)),
            dispose() {
                for (const unsubscribe of subs.splice(0))
                    unsubscribe();
            },
        };
    });
    subs.push(deps.todos.observeTodos(deps.listId).subscribe((todos) => {
        store.setState({ slice: onSnapshot(store.getState().slice, todos, (t) => t.id) });
    }));
    return store;
};
//# sourceMappingURL=todosStore.js.map